import JSZip from 'jszip';
import type { CertificateRequestFormData } from '../types/applicationForm';
import { formatYmdAsCrmDate } from '../utils/dateTime';

const TEMPLATE_URL = '/assets/ctr/certificate-request-form.xlsx';
const ITEM_START_ROW = 32;
const ITEM_MAX_ROWS = 10;
const CHECK = '√';

let cachedTemplateBytes: ArrayBuffer | null = null;

async function loadTemplateBytes(): Promise<ArrayBuffer> {
  if (cachedTemplateBytes) return cachedTemplateBytes.slice(0);
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error('Failed to load Certificate Request Excel template.');
  cachedTemplateBytes = await res.arrayBuffer();
  return cachedTemplateBytes.slice(0);
}

function escapeXmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function makeSiBlock(text: string): string {
  const needsPreserve = /^\s|\s$|\n|\r|\t/.test(text);
  const body = escapeXmlText(text);
  if (needsPreserve) return `<si><t xml:space="preserve">${body}</t></si>`;
  return `<si><t>${body}</t></si>`;
}

function extractSiBlocks(sharedStringsXml: string): string[] {
  const blocks: string[] = [];
  const re = /<si\b[^>]*>[\s\S]*?<\/si>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sharedStringsXml))) blocks.push(m[0]);
  return blocks;
}

function buildSharedStringsXml(siBlocks: string[]): string {
  const count = siBlocks.length;
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${count}" uniqueCount="${count}">` +
    siBlocks.join('') +
    '</sst>'
  );
}

function cellMatchRe(address: string): RegExp {
  return new RegExp(`<c r="${address}"[^/]*/>|<c r="${address}"[^>]*>[\\s\\S]*?</c>`);
}

function extractStyleAttr(cellXml: string): string {
  const m = /\bs="(\d+)"/.exec(cellXml);
  return m ? ` s="${m[1]}"` : '';
}

function replaceCell(sheetXml: string, address: string, replacement: string): string {
  const re = cellMatchRe(address);
  if (!re.test(sheetXml)) {
    throw new Error(`Cell ${address} missing in Certificate Request template.`);
  }
  return sheetXml.replace(re, replacement);
}

function formatDateDisplay(iso: string): string {
  const t = iso.trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return formatYmdAsCrmDate(t);
  return t;
}

/** Parse `YYYY/CTR/NNN` into year + running number for the Ref. cells. */
export function parseCertificateRequestRef(applicationNo: string): {
  year: string;
  running: string;
} {
  const trimmed = applicationNo.trim();
  const m = /^(\d{4})\s*\/\s*CTR\s*\/\s*(\d{1,3})$/i.exec(trimmed);
  if (m) {
    return { year: m[1], running: m[2].padStart(3, '0') };
  }
  const parts = trimmed.split('/').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return {
      year: parts[0] || '',
      running: (parts[parts.length - 1] || '').padStart(3, '0'),
    };
  }
  return { year: '', running: '' };
}

export function certificateRequestFileStem(applicationNo: string): string {
  const trimmed = applicationNo.trim();
  if (!trimmed) return 'certificate-request';
  return trimmed.replace(/[\\/:*?"<>|]+/g, '_');
}

function buildCellUpdates(
  form: CertificateRequestFormData,
  applicationNo: string,
  opts?: { /** PDF path: leave signature cells blank — circular seals are stamped after convert. */ omitSignatureText?: boolean },
): Record<string, string> {
  const ref = parseCertificateRequestRef(applicationNo);
  const omitSig = Boolean(opts?.omitSignatureText);
  const updates: Record<string, string> = {
    Q8: ref.year,
    U8: ref.running,
    E13: formatDateDisplay(form.date),
    E15: form.customer_no.trim(),
    O15: form.sales_code.trim(),
    E17: form.customer_name.trim(),
    E19: form.contact_person.trim(),
    O19: form.contact_no.trim(),
    E21: form.email.trim(),
    E23: form.types.food_grade_sgs ? CHECK : '',
    L23: form.types.msds ? CHECK : '',
    E25: form.types.sfa ? CHECK : '',
    L25: form.types.iso_22000 ? CHECK : '',
    E27: form.types.others ? CHECK : '',
    H27: form.types.others ? form.type_others.trim() : '',
    // Requested by = submitter, Prepared by = authorised, Verified by = approver
    B45: omitSig ? '' : form.requested_by.trim(),
    I45: omitSig ? '' : form.prepared_by.trim(),
    P45: omitSig ? '' : form.verified_by.trim(),
  };

  for (let i = 0; i < ITEM_MAX_ROWS; i += 1) {
    const row = ITEM_START_ROW + i;
    const item = form.items[i] ?? {
      item_no: '',
      vendor_no: '',
      material: '',
      remarks: '',
    };
    const itemNo = item.item_no.trim();
    const vendorNo = item.vendor_no.trim();
    const material = item.material.trim();
    const remarks = item.remarks.trim();
    const filled = Boolean(itemNo || vendorNo || material || remarks);
    updates[`B${row}`] = filled ? String(i + 1) : '';
    updates[`C${row}`] = itemNo;
    updates[`H${row}`] = vendorNo;
    updates[`K${row}`] = material;
    updates[`O${row}`] = remarks;
  }

  return updates;
}

function blobToBase64(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  });
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * Patch the official PU-040 template in place (JSZip), preserving logo/drawing
 * so Microsoft Graph `content?format=pdf` stays valid — same approach as CFF.
 */
async function buildFilledWorkbookBlob(
  form: CertificateRequestFormData,
  applicationNo: string,
  opts?: { omitSignatureText?: boolean },
): Promise<Blob> {
  const updates = buildCellUpdates(form, applicationNo, opts);
  const zip = await JSZip.loadAsync(await loadTemplateBytes());

  const sstFile = zip.file('xl/sharedStrings.xml');
  if (!sstFile) throw new Error('Certificate Request template missing sharedStrings.xml');
  const siBlocks = extractSiBlocks(await sstFile.async('string'));

  let sheetXml = await zip.file('xl/worksheets/sheet1.xml')!.async('string');
  for (const [address, value] of Object.entries(updates)) {
    const existing = sheetXml.match(cellMatchRe(address))?.[0] ?? '';
    const style = extractStyleAttr(existing);
    if (!value) {
      sheetXml = replaceCell(sheetXml, address, `<c r="${address}"${style}/>`);
      continue;
    }
    const idx = siBlocks.length;
    siBlocks.push(makeSiBlock(value));
    sheetXml = replaceCell(
      sheetXml,
      address,
      `<c r="${address}"${style} t="s"><v>${idx}</v></c>`,
    );
  }

  zip.file('xl/worksheets/sheet1.xml', sheetXml);
  zip.file('xl/sharedStrings.xml', buildSharedStringsXml(siBlocks));

  const out = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compression: 'DEFLATE',
  });
  return out;
}

export async function buildCertificateRequestExcelBase64(
  form: CertificateRequestFormData,
  applicationNo: string,
  opts?: { omitSignatureText?: boolean },
): Promise<string> {
  const blob = await buildFilledWorkbookBlob(form, applicationNo, opts);
  return blobToBase64(blob);
}

export async function downloadCertificateRequestExcel(
  form: CertificateRequestFormData,
  applicationNo: string,
): Promise<void> {
  const blob = await buildFilledWorkbookBlob(form, applicationNo);
  const stem = certificateRequestFileStem(applicationNo);
  triggerBrowserDownload(blob, `${stem}.xlsx`);
}

async function convertXlsxBase64ToPdf(xlsxBase64: string, fileStem: string): Promise<string> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: 'convert_xlsx_pdf',
      sheet_xlsx_base64: xlsxBase64,
      file_stem: fileStem,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(
      typeof body.error === 'string'
        ? body.error
        : 'PDF convert failed. Check SharePoint / Graph convert is configured.',
    );
  }
  const pdfB64 = typeof body.pdf_base64 === 'string' ? body.pdf_base64 : '';
  if (!pdfB64) throw new Error('PDF convert returned empty result.');
  return pdfB64;
}

export async function downloadCertificateRequestPdf(
  form: CertificateRequestFormData,
  applicationNo: string,
): Promise<void> {
  const stem = certificateRequestFileStem(applicationNo);
  // Blank signature cells — circular seals stamped after Graph convert (like CFF/CIT).
  const xlsxBase64 = await buildCertificateRequestExcelBase64(form, applicationNo, {
    omitSignatureText: true,
  });
  let pdfB64 = await convertXlsxBase64ToPdf(xlsxBase64, stem || 'certificate-request');
  const { stampCertificateRequestSignaturesOnPdfBase64 } = await import('./creatorNameStamp');
  pdfB64 = await stampCertificateRequestSignaturesOnPdfBase64(pdfB64, {
    requestedBy: form.requested_by,
    preparedBy: form.prepared_by,
    verifiedBy: form.verified_by,
  });
  const binary = atob(pdfB64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  triggerBrowserDownload(new Blob([bytes], { type: 'application/pdf' }), `${stem}.pdf`);
}
